import {
  Button,
  Pagination,
  ProgressBar,
  SearchBarProps,
  styled,
} from '@zeiss/beyond-online-react';
import { EmptyState } from '@zeiss/lib-components';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams } from 'react-router-dom';

import { AddMemberModal, MembersTable, OutletContextType } from './';
import { ActionBar } from 'src/components/shared/ActionBar';
import { DEFAULT_PAGE, TABLE_ITEMS_PER_PAGE } from 'src/constants';
import { useTenantUsers } from 'src/hooks/api';
import { StandardRouteParams } from 'src/types';
import { GetApiV1TenantsIdUsersParams } from 'src/types/rest/tenant';

const StyledPagination = styled(Pagination)`
  /* Remove the background colour to match the designs */
  background: none;
  box-shadow: none;

  button,
  button:disabled {
    background: none;
  }

  > div > div {
    background: none;
  }
`;

export const Members = () => {
  const { t } = useTranslation(['common']);
  const { tenantId } = useParams<StandardRouteParams>();

  const { canUpdateMembers } = useOutletContext<OutletContextType>();

  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const [pagination, setPagination] = React.useState<
    Required<Pick<GetApiV1TenantsIdUsersParams, 'itemsPerPage' | 'pageIndex'>>
  >({
    itemsPerPage: TABLE_ITEMS_PER_PAGE,
    pageIndex: DEFAULT_PAGE,
  });

  const { data, error, isPending } = useTenantUsers({
    tenantId,
    ...pagination,
    query: searchQuery,
  });

  const openAddMembersModal = () => setIsModalOpen(true);

  const closeAddMembersModal = () => setIsModalOpen(false);

  const handleSearch: SearchBarProps['onSearch'] = (_, { searchValue }) => {
    setSearchQuery(searchValue);
  };

  const renderResult = React.useCallback(() => {
    if (error) {
      return <div>Error loading data</div>;
    }

    if (isPending) {
      return <ProgressBar animate="spinning" />;
    }

    const { items: members, itemsPerPage, pageIndex, totalItems } = data;

    if (members.length) {
      return (
        <>
          <MembersTable canRemove={canUpdateMembers} data={members} />
          <StyledPagination
            onPageChanged={(pageIndex) => {
              setPagination((prev) => ({
                ...prev,
                pageIndex,
              }));
            }}
            onSizeChanged={(itemsPerPage) => {
              setPagination((prev) => {
                return {
                  ...prev,
                  itemsPerPage,
                };
              });
            }}
            pageSize={itemsPerPage}
            selectedPage={pageIndex}
            total={totalItems}
          />
        </>
      );
    }

    /* istanbul ignore else -- @preserve */
    if (members.length === 0 && !searchQuery) {
      return (
        <EmptyState
          description={t('common:emptyState.tenantMembers')}
          headline={t('common:emptyState.textNoX', {
            x: t('common:glossary.member_other'),
          })}
          illustrationConfig={{
            img: 'ClearedData',
          }}
        />
      );
    }

    return (
      <EmptyState
        description={t('common:emptyState.tryDifferentTerm')}
        headline={t('common:emptyState.noXfound', {
          x: t('common:glossary.member'),
        })}
        illustrationConfig={{
          img: 'SearchNotFound',
        }}
      />
    );
  }, [error, isPending, data]);

  return (
    <>
      <ActionBar
        onSearch={handleSearch}
        searchableFields={`${t('common:glossary.email')}, ${t('common:phone')}`}
      >
        {canUpdateMembers ? (
          <Button icon="Plus" onClick={openAddMembersModal} size="m">
            {t('common:action.addX', { x: t('common:glossary.member') })}
          </Button>
        ) : null}
      </ActionBar>

      {renderResult()}

      <AddMemberModal isOpen={isModalOpen} onClose={closeAddMembersModal} />
    </>
  );
};
